import { closestCenter, DndContext } from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import Add from '@mui/icons-material/Add';
import Close from '@mui/icons-material/Close';
import type { SortDirection, SortKey } from 'common/types/summaryDefinitions';
import { Button, IconButton } from 'components/ui';
import { SORT_DIRECTION_OPTIONS } from '../dashboardEditFields';
import type { DashboardFieldOption } from '../dashboardEditFields.types';
import {
  DashboardControlRow,
  DashboardControlStack,
  DashboardEmptyHint,
} from './dashboardControls.styles';
import DashboardEnumSelect from './DashboardEnumSelect';
import DashboardSortableRow from './DashboardSortableRow';
import { useDashboardSortableSensors } from './useDashboardSortableSensors';

interface DashboardSortKeysFieldProps {
  disabled?: boolean;
  onChange: (next: SortKey[]) => void;
  options: readonly DashboardFieldOption[];
  testId?: string;
  value: readonly SortKey[];
}

// Row identity is positional. The same column can legitimately appear twice while a user is
// mid-edit, so the column name is not a usable key — this mirrors how the spreadsheet column
// editor keys its own sort and filter rows.
const rowId = (index: number) => `sort-${index}`;

const DashboardSortKeysField = ({
  disabled = false,
  onChange,
  options,
  testId,
  value,
}: DashboardSortKeysFieldProps) => {
  const sensors = useDashboardSortableSensors();
  const ids = value.map((_, index) => rowId(index));
  // A disabled or legacy option cannot seed a new row: "Add sort key" would create a row the user
  // then has to fix. Seed from the first genuinely selectable column instead.
  const seedOption = options.find(option => !option.disabledReason);
  const canAdd = Boolean(seedOption);

  const update = (index: number, next: SortKey) =>
    onChange(value.map((entry, i) => (i === index ? next : entry)));

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) {
      return;
    }
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from === -1 || to === -1) {
      return;
    }
    onChange(arrayMove([...value], from, to));
  };

  return (
    <DashboardControlStack>
      {value.length === 0 && <DashboardEmptyHint>No sorting applied.</DashboardEmptyHint>}
      <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd} sensors={sensors}>
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          {value.map(([column, direction], index) => (
            <DashboardSortableRow
              // eslint-disable-next-line react/no-array-index-key
              key={rowId(index)}
              disabled={disabled || value.length < 2}
              id={rowId(index)}
              action={
                <IconButton
                  aria-label="Remove sort key"
                  disabled={disabled}
                  onClick={() => onChange(value.filter((_, i) => i !== index))}
                  size="small">
                  <Close fontSize="inherit" />
                </IconButton>
              }>
              <DashboardControlRow>
                <DashboardEnumSelect
                  disabled={disabled}
                  isRequired
                  onChange={next => next && update(index, [next, direction])}
                  options={options}
                  searchable={options.length > 10}
                  value={column}
                />
                <DashboardEnumSelect
                  disabled={disabled}
                  isRequired
                  onChange={next => next && update(index, [column, next as SortDirection])}
                  options={SORT_DIRECTION_OPTIONS}
                  value={direction}
                />
              </DashboardControlRow>
            </DashboardSortableRow>
          ))}
        </SortableContext>
      </DndContext>
      <div>
        <Button
          data-testid={testId}
          disabled={disabled || !canAdd}
          onClick={() => seedOption && onChange([...value, [seedOption.value, 'desc']])}
          sizeVariant="compact"
          startIcon={<Add fontSize="inherit" />}>
          Add sort key
        </Button>
      </div>
    </DashboardControlStack>
  );
};

export default DashboardSortKeysField;
